import { auth } from "./auth";

export type SessionResponse = Awaited<ReturnType<typeof auth.getSession>>;

const SESSION_TTL_MS = 30_000;

type SessionCacheEntry = {
	value: SessionResponse | null;
	expiresAt: number;
};

let sessionCache: SessionCacheEntry | null = null;
let inFlightPromise: Promise<SessionResponse | null> | null = null;

function isCacheValid() {
	return !!sessionCache && sessionCache.expiresAt > Date.now();
}

function setCacheEntry(value: SessionResponse | null) {
	sessionCache = {
		value,
		expiresAt: Date.now() + SESSION_TTL_MS,
	};
}

async function fetchSession(): Promise<SessionResponse | null> {
	try {
		const session = await auth.getSession();
		if (session.error || !session.data?.session) {
			setCacheEntry(null);
			return null;
		}
		setCacheEntry(session);
		return session;
	} catch {
		setCacheEntry(null);
		return null;
	}
}

export async function getCachedSession(
	options?: { forceRefresh?: boolean },
): Promise<SessionResponse | null> {
	const forceRefresh = options?.forceRefresh ?? false;

	if (!forceRefresh && isCacheValid()) {
		return sessionCache?.value ?? null;
	}

	if (!forceRefresh && inFlightPromise) {
		return inFlightPromise;
	}

	inFlightPromise = (async () => {
		try {
			return await fetchSession();
		} finally {
			inFlightPromise = null;
		}
	})();

	return inFlightPromise;
}

export async function getCachedAccessToken(
	options?: { forceRefresh?: boolean },
): Promise<string | null> {
	const session = await getCachedSession(options);
	return session?.data?.session?.token ?? null;
}

export function setCachedSession(session: SessionResponse | null): void {
	setCacheEntry(session);
}

export function invalidateSessionCache(): void {
	sessionCache = null;
	inFlightPromise = null;
}

